import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import WaiverAgreementForm from '@/components/WaiverAgreementForm';
import { fetchPublicWaiver, signPublicWaiver, type PublicWaiverBundle } from '@/api/publicWaiver';
import { format } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import type { WaiverAnswers } from '@/domain/types';

type Load =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'invalid' }
  | { state: 'ready'; bundle: PublicWaiverBundle };

/**
 * Customer-facing waiver signing page. Reached by a public link keyed on the
 * waiver id (`/customer-waiver/:waiverId`) — no login, like the deposit pay
 * page. It reads and signs through the public waiver endpoints (tv-k9b.11),
 * never the staff store, which a logged-out customer can't load. Renders the
 * full Addikted to Ink agreement and captures the medical declaration,
 * consents, identity and a hold-to-sign signature.
 */
export default function WaiverSignPage() {
  const { waiverId } = useParams<{ waiverId: string }>();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [justSigned, setJustSigned] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const signingRef = useRef(false);

  useEffect(() => {
    if (!waiverId) {
      setLoad({ state: 'invalid' });
      return;
    }
    let live = true;
    fetchPublicWaiver(waiverId)
      .then((bundle) => {
        if (!live) return;
        setLoad(!bundle || bundle.linkState === 'expired' ? { state: 'invalid' } : { state: 'ready', bundle });
      })
      .catch(() => {
        if (live) setLoad({ state: 'error' });
      });
    return () => {
      live = false;
    };
  }, [waiverId]);

  if (load.state === 'loading') {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center animate-pulse" data-testid="waiver-loading">
          Loading your waiver…
        </p>
      </CustomerShell>
    );
  }

  if (load.state === 'error') {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center" data-testid="waiver-load-error">
          We couldn&rsquo;t load your waiver just now. Check your connection and refresh the page.
        </p>
      </CustomerShell>
    );
  }

  if (load.state === 'invalid') {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center">
          This waiver link is invalid or has expired. Please contact the studio.
        </p>
      </CustomerShell>
    );
  }

  const { bundle } = load;
  const firstName = bundle.firstName ?? 'there';
  const booking = bundle.booking;
  const sessionDay = booking?.start ? format(new Date(booking.start), 'EEEE d MMMM') : null;

  if (justSigned || bundle.linkState === 'signed') {
    return (
      <CustomerShell>
        <div className="text-center py-8" data-testid="waiver-sign-success">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-2xl text-cream mt-4">All signed</h1>
          <p className="font-serif text-cream-soft mt-2">
            Thanks {firstName} — your medical waiver is on file
            {sessionDay ? ` for your session on ${sessionDay}` : ''}.
            {bundle.artistName ? ` See you soon, ${bundle.artistName} is looking forward to it.` : ''}
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-6">
            Nothing else to do — see you at the studio.
          </p>
        </div>
      </CustomerShell>
    );
  }

  const onSigned = (answers: WaiverAnswers) => {
    if (signingRef.current) return;
    signingRef.current = true;
    setSigning(true);
    setSignError(null);
    void signPublicWaiver(bundle.waiver.id, answers).then((r) => {
      signingRef.current = false;
      setSigning(false);
      if (r.ok) {
        setJustSigned(true);
        return;
      }
      setSignError(
        r.status === 410 || r.status === 404
          ? 'This waiver link has expired. Please contact the studio.'
          : r.status === 422
            ? 'Please confirm you are 18 or over, agree to the terms and type your name to sign.'
            : 'We couldn’t save your signature. Check your connection and try again.',
      );
    });
  };

  return (
    <CustomerShell>
      <header className="text-center mb-5">
        <p className="label-small-caps text-gold-bright">Pre-session medical waiver</p>
        <h1 className="heading-display text-2xl text-cream mt-1">Hi {firstName}</h1>
        <p className="font-serif text-cream-soft text-sm mt-2">
          Please review and sign before your{' '}
          {booking ? booking.sessionLength.replace('-', ' ') : ''} session
          {bundle.artistName ? ` with ${bundle.artistName}` : ''}
          {sessionDay ? ` on ${sessionDay}` : ''}
          {bundle.studioName ? ` at ${bundle.studioName}` : ''}.
        </p>
      </header>

      {/* The legal full name is typed by the client: only the first name is known here. */}
      <WaiverAgreementForm customer={{ firstName: '', lastName: '' }} variant="customer" onSigned={onSigned} />
      {signing ? (
        <p className="font-inter text-[11px] text-cream-soft text-center mt-3" data-testid="waiver-signing">
          Saving your signature…
        </p>
      ) : null}
      {signError ? (
        <p role="alert" className="font-inter text-[12px] text-blood text-center mt-3" data-testid="waiver-sign-error">
          {signError}
        </p>
      ) : null}
    </CustomerShell>
  );
}

function CustomerShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full bg-ink-black text-cream flex flex-col items-center px-6 py-8">
      <div className="max-w-md w-full">
        <p className="font-cinzel-deco text-xs text-gold-bright tracking-widest text-center mb-6">
          ADDIKTED TO INK
        </p>
        {children}
        <p className="font-inter text-[9px] text-cream-soft/70 text-center mt-10 uppercase tracking-widest">
          Secure signing link. Mock prototype — no data leaves the studio.
        </p>
      </div>
    </div>
  );
}
